import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getLaunchReadiness } from "@/lib/launch-readiness.functions";
import { EmailDlqPanel } from "@/components/admin/EmailDlqPanel";
import { requireAdmin } from "@/lib/admin-guard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ClipboardCheck,
  Crown,
  FileText,
  History,
  Mail,
  MinusCircle,
  RefreshCw,
  Rocket,
  
  Webhook,
  XCircle,
} from "lucide-react";

export const Route = createFileRoute("/admin_/launch-readiness")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Launch Readiness — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: LaunchReadinessPage,
  errorComponent: ({ error }) => (
    <div className="p-8 text-destructive">{error instanceof Error ? error.message : "Page unavailable"}</div>
  ),
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

function fmtAgo(iso: string | null | undefined) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function fmtAbs(iso: string | null | undefined) {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString();
}

function StatusBadge({ ok, label }: { ok: boolean; label?: string }) {
  return (
    <Badge variant={ok ? "default" : "destructive"} className="gap-1">
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {label ?? (ok ? "OK" : "Failing")}
    </Badge>
  );
}

function LaunchReadinessPage() {
  const router = useRouter();
  const fetcher = useServerFn(getLaunchReadiness);
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["launch-readiness"],
    queryFn: () => fetcher({}),
    refetchInterval: 60_000,
  });

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <button onClick={() => router.navigate({ to: "/admin" })} className="hover:underline">
              Admin
            </button>
            <span>/</span>
            <span>Launch Readiness</span>
          </div>
          <h1 className="text-3xl font-semibold flex items-center gap-2 mt-1">
            <Rocket className="h-7 w-7 text-primary" />
            Launch Readiness
          </h1>
          <p className="text-muted-foreground mt-1">
            Live counts, webhook health, email smoke-test, and SEO endpoints. Auto-refreshes every 60s.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 me-2 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="p-4 text-destructive">
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {isLoading && !data && (
        <Card><CardContent className="p-6 text-muted-foreground">Loading…</CardContent></Card>
      )}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Crown className="h-4 w-4" /> Founding Members (paid)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold">
                  {((data as any).foundingMembers?.paid ?? 0).toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Active, paid, not cancelled or refunded
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <FileText className="h-4 w-4" /> Therapist applications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-semibold">{data.applications.total.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {data.applications.paid} paid · {data.applications.live} live
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Activity className="h-4 w-4" /> Snapshot at
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm">{fmtAbs(data.generatedAt)}</div>
                <div className="text-xs text-muted-foreground mt-1">{fmtAgo(data.generatedAt)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Scripted verification helper */}
          {(data as any).checks?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ClipboardCheck className="h-4 w-4" /> Test verification (read-only)
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Runs read-only checks after each manual test. Never initiates
                  refunds, cancellations, or Stripe charges. Email rows are
                  classified as queued, provider-accepted, delivered, or
                  not-verifiable — a log row alone never counts as a delivered email.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data as any).checks.map((c: any) => {
                  const tone =
                    c.status === "Passed"
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                      : c.status === "Failed"
                      ? "border-destructive/40 bg-destructive/5 text-destructive"
                      : "border-border bg-muted/30 text-muted-foreground";
                  const Icon =
                    c.status === "Passed"
                      ? CheckCircle2
                      : c.status === "Failed"
                      ? XCircle
                      : MinusCircle;
                  return (
                    <div
                      key={c.id}
                      className={`flex items-start justify-between gap-3 rounded-md border p-3 ${tone}`}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 font-medium">
                          <Icon className="h-4 w-4" />
                          {c.title}
                        </div>
                        <div className="mt-1 text-xs opacity-90">{c.reason}</div>
                        <div className="mt-1 text-[11px] opacity-70">Checked {fmtAgo(c.at)}</div>
                      </div>
                      <Badge
                        variant={
                          c.status === "Failed"
                            ? "destructive"
                            : c.status === "Passed"
                            ? "default"
                            : "outline"
                        }
                        className="shrink-0"
                      >
                        {c.status}
                      </Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          <EmailDlqPanel />



          {/* Permanent audit log */}
          {(data as any).auditNotes?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <History className="h-4 w-4" /> Audit log
                </CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Permanent incident record. Survives across deploys.
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                {(data as any).auditNotes.map((n: any) => (
                  <div
                    key={n.id}
                    className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-medium text-sm">
                        {n.title}
                      </div>
                      <Badge variant="default">{n.status}</Badge>
                    </div>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {n.eventType} · {fmtAbs(n.at)}
                    </div>
                    <dl className="mt-2 space-y-1.5 text-xs text-foreground/85">
                      <div>
                        <dt className="font-semibold inline">Root cause: </dt>
                        <dd className="inline">{n.rootCause}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold inline">Fix: </dt>
                        <dd className="inline">{n.fix}</dd>
                      </div>
                      <div>
                        <dt className="font-semibold inline">Follow-up: </dt>
                        <dd className="inline">{n.followUp}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}


          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Webhook className="h-4 w-4" /> Stripe webhook
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground">Last processed</div>
                  <div className="font-medium">{fmtAbs(data.lastWebhook?.at)}</div>
                  <div className="text-xs text-muted-foreground">{fmtAgo(data.lastWebhook?.at)}</div>
                </div>
                {data.lastWebhook ? (
                  <StatusBadge
                    ok={data.lastWebhook.status === "success"}
                    label={`${data.lastWebhook.eventType} · ${data.lastWebhook.status}`}
                  />
                ) : (
                  <Badge variant="outline">No events yet</Badge>
                )}
              </div>
              {data.lastWebhook?.summary && (
                <div className="text-xs text-muted-foreground border-s-2 ps-3">{data.lastWebhook.summary}</div>
              )}
              {data.lastWebhookError && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                  <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                  <div>
                    <div className="font-medium text-destructive">
                      Last error · {fmtAgo(data.lastWebhookError.at)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {data.lastWebhookError.eventType}
                      {data.lastWebhookError.summary ? ` — ${data.lastWebhookError.summary}` : ""}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4" /> Email smoke-test
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground">Last run</div>
                  <div className="font-medium">{fmtAbs(data.lastEmailSmokeTest?.at)}</div>
                  <div className="text-xs text-muted-foreground">{fmtAgo(data.lastEmailSmokeTest?.at)}</div>
                </div>
                {data.lastEmailSmokeTest ? (
                  <StatusBadge ok={data.lastEmailSmokeTest.status === "ok"} />
                ) : (
                  <Badge variant="outline">Never run</Badge>
                )}
              </div>
              {data.lastEmailSmokeTest?.details && (
                <div className="text-xs text-muted-foreground">
                  {(data.lastEmailSmokeTest.details as any).succeeded ?? 0} succeeded ·{" "}
                  {(data.lastEmailSmokeTest.details as any).failed ?? 0} failed ·{" "}
                  to {(data.lastEmailSmokeTest.details as any).to ?? "—"}
                </div>
              )}
              <Button
                variant="link"
                size="sm"
                className="px-0"
                onClick={() => router.navigate({ to: "/admin", search: { tab: "email-tests" } as any })}
              >
                Run smoke-test →
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" /> SEO endpoints
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { name: "Sitemap", info: data.seo.sitemap },
                { name: "Robots", info: data.seo.robots },
              ].map(({ name, info }) => (
                <div key={name} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{name}</div>
                    <a
                      href={info.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-muted-foreground hover:underline truncate block"
                    >
                      {info.url}
                    </a>
                    <div className="text-xs text-muted-foreground">
                      HTTP {info.status} · {info.contentType ?? "—"} · {info.bytes} bytes
                    </div>
                  </div>
                  <StatusBadge ok={info.ok} label={info.ok ? `${info.status} OK` : `${info.status || "ERR"}`} />
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
